import * as React from "react";
import { cn } from "@/lib/utils";
import { Button } from "./Button";

interface AlertDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	children: React.ReactNode;
}

interface AlertDialogContentProps {
	children: React.ReactNode;
	className?: string;
}

interface AlertDialogHeaderProps {
	children?: React.ReactNode;
	className?: string;
}

interface AlertDialogTitleProps {
	children?: React.ReactNode;
	className?: string;
}

interface AlertDialogDescriptionProps {
	children?: React.ReactNode;
	className?: string;
}

interface AlertDialogFooterProps {
	children?: React.ReactNode;
	className?: string;
}

interface AlertDialogActionProps {
	children: React.ReactNode;
	onClick?: () => void;
	variant?: "default" | "destructive";
	className?: string;
	disabled?: boolean;
}

interface AlertDialogCancelProps {
	children: React.ReactNode;
	onClick?: () => void;
	className?: string;
	disabled?: boolean;
}

const AlertDialog: React.FC<AlertDialogProps> = ({
	open,
	onOpenChange,
	children,
}) => {
	if (!open) return null;

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center">
			{/* Backdrop */}
			<div
				className="fixed inset-0 bg-black/50 backdrop-blur-sm"
				onClick={() => onOpenChange(false)}
			/>
			{/* Content */}
			<div className="relative z-50">{children}</div>
		</div>
	);
};

const AlertDialogContent: React.FC<AlertDialogContentProps> = ({
	children,
	className,
}) => (
	<div
		className={cn(
			"bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 max-w-md w-full mx-4 p-6",
			className,
		)}
	>
		{children}
	</div>
);

const AlertDialogHeader: React.FC<AlertDialogHeaderProps> = ({
	children,
	className,
}) => <div className={cn("mb-4", className)}>{children}</div>;

const AlertDialogTitle: React.FC<AlertDialogTitleProps> = ({
	children,
	className,
}) => (
	<h2
		className={cn(
			"text-lg font-semibold text-gray-900 dark:text-white",
			className,
		)}
	>
		{children}
	</h2>
);

const AlertDialogDescription: React.FC<AlertDialogDescriptionProps> = ({
	children,
	className,
}) => (
	<p className={cn("text-sm text-gray-600 dark:text-gray-400 mt-2", className)}>
		{children}
	</p>
);

const AlertDialogFooter: React.FC<AlertDialogFooterProps> = ({
	children,
	className,
}) => (
	<div className={cn("flex justify-end space-x-3 mt-6", className)}>
		{children}
	</div>
);

const AlertDialogAction: React.FC<AlertDialogActionProps> = ({
	children,
	onClick,
	variant = "default",
	className,
	disabled,
}) => (
	<Button
		onClick={onClick}
		variant={variant === "destructive" ? "destructive" : "default"}
		className={className}
		disabled={disabled}
	>
		{children}
	</Button>
);

const AlertDialogCancel: React.FC<AlertDialogCancelProps> = ({
	children,
	onClick,
	className,
	disabled,
}) => (
	<Button
		onClick={onClick}
		variant="outline"
		className={className}
		disabled={disabled}
	>
		{children}
	</Button>
);

export {
	AlertDialog,
	AlertDialogContent,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogAction,
	AlertDialogCancel,
};
